import React, { useEffect } from 'react';

const TermsOfService = () => {
  useEffect(() => {
    document.title = 'Terms of Service | Federal IP Office - Legal Services';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms of Service for Federal IP Office. Review the terms and conditions for using our legal services.');
    }
  }, []);

  return (
    <div className="pt-20" data-testid="terms-page">
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-[#2C3E50] font-bold mb-6">
            Terms of Service
          </h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Agreement to Terms</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              These Terms of Service constitute a legally binding agreement between you and Federal IP Office ("we," "us," or "our") concerning your access to and use of our website and legal services. By accessing or using our website, you agree to be bound by these Terms.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">No Attorney-Client Relationship</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              Use of this website and the information provided does not create an attorney-client relationship between you and Federal IP Office. An attorney-client relationship is established only through a signed engagement letter or retainer agreement. Information submitted through contact forms or inquiries is not confidential until such relationship is established.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Legal Services</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-4">
              Our legal services are provided subject to the following conditions:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-[#2E2E2E]/80 ml-4">
              <li>Services are provided only after execution of an engagement agreement</li>
              <li>Fees and billing terms are outlined in the engagement agreement</li>
              <li>Scope of representation is limited to matters specified in the agreement</li>
              <li>We do not guarantee specific outcomes or results</li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Website Use</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-4">
              You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-[#2E2E2E]/80 ml-4">
              <li>Use the website in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the website</li>
              <li>Transmit any harmful code, viruses, or malicious software</li>
              <li>Collect or harvest information about other users</li>
              <li>Impersonate or misrepresent your affiliation with any person or entity</li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Intellectual Property Rights</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              The website and its content, including but not limited to text, graphics, logos, and software, are the property of Federal IP Office and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Information Accuracy</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              While we strive to provide accurate and up-to-date information on our website, we make no representations or warranties about the completeness, accuracy, or reliability of any information. Legal information may become outdated, and laws vary by jurisdiction. This website does not constitute legal advice.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Limitation of Liability</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              To the fullest extent permitted by law, Federal IP Office shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the website or our services. Our total liability shall not exceed the amount paid by you for our services.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Indemnification</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              You agree to indemnify and hold harmless Federal IP Office and its attorneys from any claims, damages, losses, liabilities, and expenses arising out of your use of the website or violation of these Terms.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Third-Party Links</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of use of these external sites. Access to third-party websites is at your own risk.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Governing Law</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts located in Ventura County, California.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Professional Conduct</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              Our attorneys are bound by the Rules of Professional Conduct applicable in the jurisdictions where they are licensed. We maintain professional liability insurance as required by law.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Modifications to Terms</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website after changes constitutes acceptance of the modified Terms.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Severability</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Contact Information</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              For questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-4 p-6 bg-[#F8F9FA] border border-[#2C3E50]/10">
              <p className="font-semibold text-[#2C3E50]">Federal IP Office</p>
              <p className="text-[#2E2E2E]/80">550 W B St, San Diego, CA 92101</p>
              <p className="text-[#2E2E2E]/80">Email: Info@federalipoffice.com</p>
              <p className="text-[#2E2E2E]/80">Phone: <a href="tel:+18335187356" className="hover:text-[#2C3E50]">+1 (833) 518-7356 </a></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
